// src/utils/exportUtils.js
// ✅ Export CSV e Excel senza librerie esterne pesanti

const CATEGORY_LABELS = {
    MATERIALS: 'Materiali', EQUIPMENT: 'Attrezzature', LABOR: 'Manodopera',
    FUEL: 'Carburante', SUBCONTRACT: 'Subappalto', TRANSPORT: 'Trasporto',
    PERMITS: 'Permessi', ADMIN: 'Amministrazione', OTHER: 'Altro',
};

const STATUS_LABELS = {
    DRAFT: 'Bozza', PLANNED: 'Pianificato', IN_PROGRESS: 'In Corso',
    ON_HOLD: 'In Attesa', DELAYED: 'Ritardato', COMPLETED: 'Completato', CANCELLED: 'Cancellato',
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '';
const formatNum = (n) => n ? Math.round(n).toString() : '0';

// ==================== CSV ====================
const toCSV = (headers, rows) => {
    const escape = (v) => {
        const str = v === null || v === undefined ? '' : String(v);
        return str.includes(',') || str.includes('"') || str.includes('\n')
            ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const lines = [
        headers.map(escape).join(','),
        ...rows.map(row => row.map(escape).join(','))
    ];
    return '\uFEFF' + lines.join('\r\n'); // BOM per Excel
};

const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
};

// ==================== EXCEL (XLSX via HTML table trick) ====================
const downloadXLS = (headers, rows, sheetName, filename) => {
    const escape = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const headerRow = headers.map(h => `<th style="background:#1e3a5f;color:#fff;font-weight:bold;padding:8px;border:1px solid #ccc">${escape(h)}</th>`).join('');
    const dataRows = rows.map((row, i) =>
        `<tr style="background:${i % 2 === 0 ? '#ffffff' : '#f8fafc'}">${row.map(cell => `<td style="padding:6px 8px;border:1px solid #e2e8f0">${escape(cell)}</td>`).join('')
        }</tr>`
    ).join('');

    const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
    <head><meta charset="UTF-8"><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
    <x:Name>${sheetName}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
    </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head>
    <body><table>${headerRow ? `<thead><tr>${headerRow}</tr></thead>` : ''}<tbody>${dataRows}</tbody></table></body></html>`;

    const blob = new Blob(['\uFEFF' + html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
};

// ==================== EXPORT SPESE ====================
export const exportExpenses = (expenses, currency = 'GNF', format = 'csv') => {
    const headers = [
        'Riferimento', 'Descrizione', 'Progetto', 'Categoria',
        'Importo', 'IVA %', 'Totale', 'Valuta',
        'Fornitore', 'N° Fattura', 'Data Fattura', 'Data Pagamento',
        'Metodo Pagamento', 'Validata', 'Note'
    ];

    const rows = expenses.map(e => [
        e.reference || '',
        e.description || '',
        e.project?.name || '',
        CATEGORY_LABELS[e.category] || e.category || '',
        formatNum(e.amount),
        formatNum(e.taxRate),
        formatNum(e.totalAmount),
        currency,
        e.supplierName || '',
        e.invoiceNumber || '',
        formatDate(e.invoiceDate),
        formatDate(e.paymentDate),
        e.paymentMethod || '',
        e.validated ? 'Sì' : 'No',
        e.notes || '',
    ]);

    const date = new Date().toISOString().split('T')[0];
    if (format === 'xlsx') {
        downloadXLS(headers, rows, 'Spese', `spese_${date}.xls`);
    } else {
        downloadCSV(toCSV(headers, rows), `spese_${date}.csv`);
    }
};

// ==================== EXPORT PROGETTI ====================
export const exportProjects = (projects, currency = 'GNF', format = 'csv') => {
    const headers = [
        'Codice', 'Nome', 'Stato', 'Priorità',
        'Budget Stimato', 'Costo Attuale', 'Scostamento',
        'Progresso %', 'Valuta',
        'Data Inizio', 'Data Fine', 'Località', 'Note'
    ];

    const rows = projects.map(p => {
        const diff = (p.estimatedBudget || 0) - (p.actualCost || 0);
        return [
            p.code || '',
            p.name || '',
            STATUS_LABELS[p.status] || p.status || '',
            p.priority || '',
            formatNum(p.estimatedBudget),
            formatNum(p.actualCost),
            formatNum(diff),
            formatNum(p.progress),
            p.currency || currency,
            formatDate(p.startDate),
            formatDate(p.endDate),
            p.location || '',
            p.description || '',
        ];
    });

    const date = new Date().toISOString().split('T')[0];
    if (format === 'xlsx') {
        downloadXLS(headers, rows, 'Progetti', `progetti_${date}.xls`);
    } else {
        downloadCSV(toCSV(headers, rows), `progetti_${date}.csv`);
    }
};

// ==================== EXPORT REPORT BUDGET ====================
export const exportBudgetReport = (projects, expenses, currency = 'GNF', format = 'csv') => {
    // Sheet 1: Riepilogo per progetto
    const headers = [
        'Progetto', 'Stato', 'Budget', 'Speso', 'Rimanente', '% Utilizzo',
        'N° Spese', 'Spese Validate', 'Progresso %'
    ];

    const rows = projects.map(p => {
        const projExpenses = expenses.filter(e => e.projectId === p.id);
        const validated = projExpenses.filter(e => e.validated).length;
        const remaining = (p.estimatedBudget || 0) - (p.actualCost || 0);
        const pct = p.estimatedBudget > 0 ? ((p.actualCost / p.estimatedBudget) * 100).toFixed(1) : '0';
        return [
            p.name,
            STATUS_LABELS[p.status] || p.status,
            formatNum(p.estimatedBudget),
            formatNum(p.actualCost),
            formatNum(remaining),
            pct + '%',
            projExpenses.length,
            `${validated}/${projExpenses.length}`,
            (p.progress || 0) + '%',
        ];
    });

    // Riga totali
    const totalBudget = projects.reduce((s, p) => s + (p.estimatedBudget || 0), 0);
    const totalSpent = projects.reduce((s, p) => s + (p.actualCost || 0), 0);
    rows.push([
        'TOTALE', '', formatNum(totalBudget), formatNum(totalSpent),
        formatNum(totalBudget - totalSpent),
        totalBudget > 0 ? ((totalSpent / totalBudget) * 100).toFixed(1) + '%' : '0%',
        expenses.length, expenses.filter(e => e.validated).length + '/' + expenses.length, ''
    ]);

    const date = new Date().toISOString().split('T')[0];
    if (format === 'xlsx') {
        downloadXLS(headers, rows, 'Report Budget', `report_budget_${date}.xls`);
    } else {
        downloadCSV(toCSV(headers, rows), `report_budget_${date}.csv`);
    }
};